import React, { useEffect, useRef } from 'react';
import { LineChart } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const ChartsPanel = () => {
  const tempCanvasRef = useRef(null);
  const salCanvasRef = useRef(null);

  const tempData = [
    [0, 23.4],
    [50, 22.1],
    [100, 20.0],
    [150, 17.6],
    [200, 15.9],
    [250, 14.2],
    [300, 12.8],
  ];

  const salData = [
    [0, 35.1],
    [50, 35.0],
    [100, 34.7],
    [150, 34.6],
    [200, 34.5],
    [250, 34.4],
    [300, 34.3],
  ];

  const drawChart = (canvas, data, color, yLabel, yMin, yMax) => {
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    // Ensure canvas has a minimum width, fallback to parent or 600px
    const canvasWidth = canvas.offsetWidth || canvas.parentElement?.offsetWidth || 600;
    const width = canvas.width = canvasWidth * 2;
    const height = 400;
    canvas.height = height;
    ctx.scale(2, 2);

    const padding = { top: 20, right: 40, bottom: 40, left: 50 };
    const chartWidth = width / 2 - padding.left - padding.right;
    const chartHeight = height / 2 - padding.top - padding.bottom;

    ctx.clearRect(0, 0, width / 2, height / 2);

    // Grid
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 5; i++) {
      const y = padding.top + (i / 5) * chartHeight;
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(padding.left + chartWidth, y);
      ctx.stroke();
    }

    // Axes
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(padding.left, padding.top);
    ctx.lineTo(padding.left, padding.top + chartHeight);
    ctx.lineTo(padding.left + chartWidth, padding.top + chartHeight);
    ctx.stroke();

    // Labels
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.font = '10px IBM Plex Mono, monospace';
    ctx.textAlign = 'right';

    // Y-axis labels
    for (let i = 0; i <= 5; i++) {
      const value = yMax - (i / 5) * (yMax - yMin);
      const y = padding.top + (i / 5) * chartHeight;
      ctx.fillText(value.toFixed(1), padding.left - 10, y + 3);
    }

    // X-axis labels
    ctx.textAlign = 'center';
    const xSteps = 6;
    for (let i = 0; i <= xSteps; i++) {
      const value = (i / xSteps) * 300;
      const x = padding.left + (i / xSteps) * chartWidth;
      ctx.fillText(value.toFixed(0), x, padding.top + chartHeight + 20);
    }

    // Axis titles
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.font = '11px IBM Plex Mono, monospace';
    ctx.textAlign = 'center';
    ctx.fillText('Depth (m)', padding.left + chartWidth / 2, height / 2 - 5);

    ctx.save();
    ctx.translate(15, padding.top + chartHeight / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.textAlign = 'center';
    ctx.fillText(yLabel, 0, 0);
    ctx.restore();

    // Plot data
    ctx.strokeStyle = color;
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();

    data.forEach((point, idx) => {
      const x = padding.left + (point[0] / 300) * chartWidth;
      const y = padding.top + chartHeight - ((point[1] - yMin) / (yMax - yMin)) * chartHeight;
      if (idx === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });

    ctx.stroke();

    // Plot points
    ctx.fillStyle = color;
    data.forEach((point) => {
      const x = padding.left + (point[0] / 300) * chartWidth;
      const y = padding.top + chartHeight - ((point[1] - yMin) / (yMax - yMin)) * chartHeight;
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, Math.PI * 2);
      ctx.fill();
    });
  };

  useEffect(() => {
    // Small delay to ensure canvas has proper dimensions after layout
    const timer = setTimeout(() => {
      drawChart(tempCanvasRef.current, tempData, 'hsl(var(--chart-1))', '°C', 10, 25);
      drawChart(salCanvasRef.current, salData, 'hsl(var(--chart-3))', 'PSU', 34, 36);
    }, 100);

    // Redraw on window resize
    const handleResize = () => {
      drawChart(tempCanvasRef.current, tempData, 'hsl(var(--chart-1))', '°C', 10, 25);
      drawChart(salCanvasRef.current, salData, 'hsl(var(--chart-3))', 'PSU', 34, 36);
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <Card className="col-span-12 animate-fade-in" data-testid="charts-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <LineChart className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">Oceanographic Profiles</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div data-testid="chart-temperature">
            <h4 className="text-sm font-medium mb-3 text-muted-foreground">Temperature vs Depth</h4>
            <div className="rounded-md bg-secondary/20 p-4">
              <canvas ref={tempCanvasRef} className="w-full" data-testid="temp-canvas" role="img" aria-label="Temperature vs depth profile chart showing ocean temperature decreasing from 23.4 degrees Celsius at surface to 12.8 degrees at 300 meters depth" />
            </div>
          </div>
          <div data-testid="chart-salinity">
            <h4 className="text-sm font-medium mb-3 text-muted-foreground">Salinity vs Depth</h4>
            <div className="rounded-md bg-secondary/20 p-4">
              <canvas ref={salCanvasRef} className="w-full" data-testid="sal-canvas" role="img" aria-label="Salinity vs depth profile chart showing ocean salinity decreasing from 35.1 PSU at surface to 34.3 PSU at 300 meters depth" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ChartsPanel;
